import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { PremiumBadge } from '../../components/ui/premium-badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Spinner } from '../../components/ui/spinner';
import { Sparkles, Star, Users, MessageSquare, Bell, CreditCard, ArrowUpCircle, Zap, TrendingUp } from 'lucide-react';

// Lazy-load tab components for faster initial page load
const LoyaltyPointsTab = lazy(() => import('./marketing/LoyaltyPointsTab'));
const ReferralsTab = lazy(() => import('./marketing/ReferralsTab'));
const SmsCampaignsTab = lazy(() => import('./marketing/SmsCampaignsTab'));
const RemindersTab = lazy(() => import('./marketing/RemindersTab'));
const CreditsTab = lazy(() => import('./marketing/CreditsTab'));

const TabFallback = () => <div className="flex items-center justify-center h-32"><Spinner /></div>;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const creditCards = [
  { key: 'balance', altKey: 'current_balance', icon: Zap, gradient: 'from-blue-700 to-blue-900', glow: 'shadow-blue-800/20', labelKey: 'marketing_credits_balance', fallback: 'Balance' },
  { key: 'total_used', altKey: 'total_used_this_cycle', icon: TrendingUp, gradient: 'from-orange-500 to-orange-700', glow: 'shadow-orange-600/20', labelKey: 'marketing_credits_used', fallback: 'Total Used' },
  { key: 'total_purchased', altKey: 'total_topped_up_this_cycle', icon: ArrowUpCircle, gradient: 'from-emerald-600 to-green-800', glow: 'shadow-green-700/20', labelKey: 'marketing_credits_purchased', fallback: 'Total Purchased' },
];

export default function MarketingToolsPage() {
  const { t, language } = useLanguage();
  const { isPremium } = useAuth();
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const settingsPath = garageSlug ? `/${garageSlug}/admin/settings?tab=subscription` : '/admin/settings?tab=subscription';
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'loyalty');
  const [tabCounts, setTabCounts] = useState({ new_referrals: 0, pending_parts_requests: 0, scheduled_campaigns: 0 });

  const tr = (key, fallback) => {
    const translated = t(key);
    return !translated || translated === key ? fallback : translated;
  };

  const fetchCredits = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/credits`);
      setCredits(data);
    } catch (err) {
      setCredits({ balance: 0, total_purchased: 0, total_used: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
    axios.get(`${API}/marketing/tab-counts`).then(r => setTabCounts(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      </AdminLayout>
    );
  }

  const isUpgradeNeeded = (credits?.balance ?? credits?.current_balance ?? 0) === 0 && (credits?.total_purchased ?? credits?.total_topped_up_this_cycle ?? 0) === 0;

  return (
    <AdminLayout>
      <div className="space-y-6 premium-slide-up">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-800 to-blue-900 px-5 py-3.5 md:px-6 md:py-4 text-white premium-glow">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/20 blur-2xl" />
            <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
          </div>
          <div className="relative z-10 flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight font-['Barlow_Condensed'] leading-tight">
                {t('marketing_title')}
              </h1>
              <p className="text-white/70 text-xs md:text-sm max-w-3xl">
                {t('marketing_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Upgrade Prompt */}
        {isUpgradeNeeded && (
          <Card
            className="border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50 shadow-md premium-scale cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(settingsPath)}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-xl bg-amber-100">
                  <ArrowUpCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">
                    {t('marketing_upgrade_required')}
                  </h3>
                  <p className="text-amber-700 text-sm mt-1">{t('marketing_upgrade_message')}</p>
                  <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                    <Star className="w-3.5 h-3.5" /> {t('marketing_loyalty_free')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low Credit Warning Banner */}
        {credits && !isUpgradeNeeded && (credits.balance ?? credits.current_balance ?? 0) <= 50 && (credits.balance ?? credits.current_balance ?? 0) > 0 && (
          <Card
            className="border-orange-300/60 bg-gradient-to-r from-orange-50 to-amber-50 shadow-md premium-scale cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('credits')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-orange-100 shrink-0">
                  <CreditCard className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-orange-900 text-sm">
                    {language === 'en' ? 'Low Credit Balance' : 'ক্রেডিট ব্যালেন্স কম'}
                  </h3>
                  <p className="text-orange-700 text-xs mt-0.5">
                    {language === 'en'
                      ? `Only ${credits.balance ?? credits.current_balance ?? 0} credits remaining. Top up to continue using SMS campaigns and reminders.`
                      : `মাত্র ${credits.balance ?? credits.current_balance ?? 0} ক্রেডিট বাকি আছে। এসএমএস ক্যাম্পেইন ও রিমাইন্ডার চালু রাখতে টপ-আপ করুন।`}
                  </p>
                </div>
                <div className="shrink-0">
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold">
                    <ArrowUpCircle className="w-3.5 h-3.5" />
                    {language === 'en' ? 'Top Up' : 'টপ-আপ'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credits Summary Cards */}
        {credits && (
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {creditCards.map(({ key, altKey, icon: Icon, gradient, glow, labelKey, fallback }, i) => (
              <Card key={key} className={`credit-card-pop premium-card-hero bg-gradient-to-br ${gradient} text-white shadow-lg ${glow} cursor-pointer transition-all duration-300 border-0`} style={{ animationDelay: `${i * 80}ms` }} onClick={() => setActiveTab('credits')}>
                <CardContent className="p-4 text-center relative overflow-hidden">
                  <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10 blur-xl" />
                  <div className="absolute -left-2 -bottom-2 w-12 h-12 rounded-full bg-white/5 blur-lg" />
                  <div className="relative z-10">
                    <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-2">
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-3xl font-extrabold tracking-tight drop-shadow-sm">{credits[key] ?? credits[altKey] ?? 0}</p>
                    <p className="text-[11px] text-white/75 mt-1 font-medium uppercase tracking-wider">{tr(labelKey, fallback)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 h-12 bg-muted/60 p-1 rounded-xl">
            <TabsTrigger value="loyalty" className="flex items-center gap-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-800 transition-all duration-200">
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">{t('marketing_loyalty')}</span>
              {!isPremium && <PremiumBadge size="sm" />}
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex items-center gap-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-800 transition-all duration-200">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">{t('marketing_referrals')}</span>
              {!isPremium && <PremiumBadge size="sm" />}
              {isPremium && tabCounts.new_referrals > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-blue-600 text-white">{tabCounts.new_referrals}</span>}
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-800 transition-all duration-200">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">{t('marketing_campaigns')}</span>
              {!isPremium && <PremiumBadge size="sm" />}
              {isPremium && (tabCounts.pending_parts_requests > 0 || tabCounts.scheduled_campaigns > 0) && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-amber-500 text-white">{(tabCounts.pending_parts_requests || 0) + (tabCounts.scheduled_campaigns || 0)}</span>}
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-800 transition-all duration-200">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">{t('marketing_reminders')}</span>
              {!isPremium && <PremiumBadge size="sm" />}
            </TabsTrigger>
            <TabsTrigger value="credits" className="flex items-center gap-1.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-800 transition-all duration-200">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">{t('marketing_credits')}</span>
              {!isPremium && <PremiumBadge size="sm" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="loyalty"><Suspense fallback={<TabFallback />}><PremiumOverlay feature={language === 'en' ? 'Loyalty Points' : 'লয়াল্টি পয়েন্ট'} locked={!isPremium}><LoyaltyPointsTab /></PremiumOverlay></Suspense></TabsContent>
          <TabsContent value="referrals"><Suspense fallback={<TabFallback />}><PremiumOverlay feature={language === 'en' ? 'Referral Program' : 'রেফারেল প্রোগ্রাম'} locked={!isPremium}><ReferralsTab /></PremiumOverlay></Suspense></TabsContent>
          <TabsContent value="campaigns"><Suspense fallback={<TabFallback />}><PremiumOverlay feature={language === 'en' ? 'SMS Campaigns' : 'এসএমএস ক্যাম্পেইন'} locked={!isPremium}><SmsCampaignsTab onCreditsChanged={fetchCredits} credits={credits} /></PremiumOverlay></Suspense></TabsContent>
          <TabsContent value="reminders"><Suspense fallback={<TabFallback />}><PremiumOverlay feature={language === 'en' ? 'Smart Reminders' : 'স্মার্ট রিমাইন্ডার'} locked={!isPremium}><RemindersTab onCreditsChanged={fetchCredits} /></PremiumOverlay></Suspense></TabsContent>
          <TabsContent value="credits"><Suspense fallback={<TabFallback />}><PremiumOverlay feature={language === 'en' ? 'SMS Credits' : 'এসএমএস ক্রেডিট'} locked={!isPremium}><CreditsTab credits={credits} onCreditsChanged={fetchCredits} /></PremiumOverlay></Suspense></TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
